import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from "@headlessui/react";
import clsx from "clsx";
import debounce from "lodash/debounce";
import {
  ComponentPropsWithoutRef,
  Fragment,
  forwardRef,
  useState,
} from "react";

import type { CustomField } from "../../types/customFields";
import ErrorMessage from "../ErrorMessage";
import { Icon } from "../Icon";
import Input, { Field } from "../Input";
import { Label } from "../Label";

const DescribedListboxButton = forwardRef<
  HTMLButtonElement,
  ComponentPropsWithoutRef<"button"> & { describedBy?: string }
>(function DescribedListboxButton({ describedBy, ...props }, ref) {
  return <button {...props} aria-describedby={describedBy} ref={ref} />;
});

export function DropdownField({
  fieldId,
  customField,
  fieldValues,
  isReadOnly,
  isRequired,
  error,
  isDebounce,
  onChange,
}: {
  customField: CustomField;
  error?: string;
  fieldId: string;
  fieldValues: CustomField["defaultValues"];
  isDebounce?: boolean;
  isReadOnly: boolean;
  isRequired?: boolean;
  onChange: (
    fieldId: string,
    customField: CustomField,
    value: CustomField["defaultValues"],
    groupId: string,
  ) => void;
}) {
  const [internalValues, setInternalValues] = useState(fieldValues);

  const errorId = `${fieldId}-error`;

  const debouncedOnChange = debounce(
    (
      fieldId: string,
      customField: CustomField,
      updatedValues: CustomField["defaultValues"],
      groupId: string,
    ) => {
      onChange(fieldId, customField, updatedValues, groupId);
    },
    1000,
  );

  const onChangeHandler = (optionValue: string, isChecked: boolean) => {
    const currentValues = internalValues || [];

    let updatedValues;
    if (isChecked) {
      const existingValue = currentValues.find((v) => v.value === optionValue);
      if (!existingValue) {
        updatedValues = [
          ...currentValues,
          { isSelected: true, value: optionValue },
        ];
      } else {
        updatedValues = currentValues.map((v) =>
          v.value === optionValue ? { ...v, isSelected: true } : v,
        );
      }
    } else {
      updatedValues = currentValues
        .map((v) => (v.value === optionValue ? { ...v, isSelected: false } : v))
        .filter((v) => v.isSelected);
    }

    setInternalValues(updatedValues);

    if (isDebounce) {
      debouncedOnChange(
        fieldId,
        customField,
        updatedValues,
        customField.groupId,
      );
    } else {
      onChange(fieldId, customField, updatedValues, customField.groupId);
    }
  };

  const selectedValues = internalValues
    ? internalValues.filter((v) => v.isSelected).map((v) => String(v.value))
    : [];

  const removeSelectedItem = (optionValue: string) => {
    onChangeHandler(optionValue, false);
  };

  return (
    <Field>
      <Label required={isRequired}>{customField.name}</Label>
      <div className="relative w-full">
        <Listbox disabled={isReadOnly}>
          {({ open }) => (
            <>
              <ListboxButton
                as={DescribedListboxButton}
                describedBy={error ? errorId : undefined}
                aria-required={isRequired}
                aria-invalid={!!error}
                className={clsx(
                  "block w-full rounded-md border px-3 py-2 text-gray-900 shadow-sm sm:text-sm",
                  error
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500 focus:outline-none"
                    : "border-gray-300 focus:border-blue-500 focus:ring-blue-500 focus:outline-none",
                  "hover:border-gray-400 transition-colors duration-200",
                  "flex items-center justify-between min-h-[38px]",
                  isReadOnly &&
                    "disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500",
                )}
              >
                <div className="flex items-center flex-wrap gap-1 flex-1">
                  {selectedValues.length === 0 ? (
                    <span className="text-gray-500">
                      Select {customField.name}
                    </span>
                  ) : (
                    selectedValues.map((value) => {
                      const option = customField.options?.find(
                        (opt) => opt.value === value,
                      );
                      const displayLabel = option?.label || value;
                      return (
                        <span
                          key={value}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-gray-100 text-black text-[10px] lg:text-xs"
                        >
                          {displayLabel}
                          {!isReadOnly && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeSelectedItem(value);
                              }}
                              className="hover:bg-gray-200 rounded-full p-0.5"
                            >
                              <Icon
                                name="xMark"
                                className="h-3 w-3 text-black"
                              />
                            </button>
                          )}
                        </span>
                      );
                    })
                  )}
                </div>
                {!isReadOnly && (
                  <Icon
                    name="chevronDown"
                    className={clsx(
                      "text-gray-400 transition-all duration-200 h-4 w-4 ml-2 flex-shrink-0",
                      open && "rotate-180",
                    )}
                  />
                )}
              </ListboxButton>

              <Transition
                show={open}
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <ListboxOptions
                  static
                  className="absolute z-50 mt-1 w-full rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                >
                  <div className="px-3 py-2 border-b border-gray-200">
                    <div className="text-xs lg:text-sm font-medium text-gray-700 mb-2">
                      Option Types
                    </div>
                    <div className="space-y-2">
                      {customField.options?.map((option, index) => {
                        const isChecked = selectedValues.includes(option.value);
                        return (
                          <ListboxOption
                            key={`${option.value}-${index}`}
                            value={option.value}
                            className="cursor-pointer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div onClick={(e) => e.stopPropagation()}>
                              <Input
                                type="checkbox"
                                rightLabel={option.label}
                                value={isChecked ? "true" : "false"}
                                readOnly={false}
                                onChange={(value) => {
                                  onChangeHandler(
                                    option.value,
                                    value === "true",
                                  );
                                }}
                              />
                            </div>
                          </ListboxOption>
                        );
                      })}
                    </div>
                  </div>
                </ListboxOptions>
              </Transition>
            </>
          )}
        </Listbox>

        {customField.helpText && (
          <p className="mt-1 text-xs lg:text-sm text-gray-500">
            {customField.helpText}
          </p>
        )}
        {error && <ErrorMessage id={errorId} message={error} />}
      </div>
    </Field>
  );
}
